/** 通用数学工具。纯函数，无副作用。 */

export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * clamp(t, 0, 1);
}

export function roundTo(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function safeNumber(value: unknown, fallback: number): number {
  if (isFiniteNumber(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

export function safeBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true') return true;
  if (value === 0 || value === '0' || value === 'false') return false;
  return fallback;
}

export function safeString(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

/** 归一化角度到 [0, 360)。 */
export function wrapAngleDeg(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  const wrapped = deg % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}

/** 两个角度之间的最短差值（度，范围 (-180, 180]）。 */
export function angleDeltaDeg(from: number, to: number): number {
  let delta = wrapAngleDeg(to) - wrapAngleDeg(from);
  if (delta > 180) delta -= 360;
  if (delta <= -180) delta += 360;
  return delta;
}

/** 角度插值，走最短路径。 */
export function lerpAngleDeg(a: number, b: number, t: number): number {
  return wrapAngleDeg(a + angleDeltaDeg(a, b) * clamp(t, 0, 1));
}

/** 由横滚/俯仰计算倾角（度）。 */
export function tiltDeg(rollDeg: number, pitchDeg: number): number {
  const roll = (rollDeg * Math.PI) / 180;
  const pitch = (pitchDeg * Math.PI) / 180;
  const cosTilt = Math.cos(roll) * Math.cos(pitch);
  const clamped = clamp(cosTilt, -1, 1);
  return (Math.acos(clamped) * 180) / Math.PI;
}

/** 指数移动平均，用于把抖动的高频采样平滑成可读数值。 */
export function ema(previous: number | undefined, next: number, alpha: number): number {
  if (previous === undefined || !Number.isFinite(previous)) return next;
  return previous + (next - previous) * clamp(alpha, 0, 1);
}
